/**
 * The one way to float a panel next to something: dropdown lists, menus,
 * suggestions, popovers. Use it for every one, never an `absolute` box inside
 * the page.
 *
 * Rendered in a portal, so no dialog's scrolling body or `overflow: hidden`
 * clips it and no stacking order hides it. Placed under its anchor, or above
 * it when the room below is short, and it follows the anchor while anything
 * scrolls. A mousedown outside both the anchor and the panel dismisses it.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

const GAP = 4;
const MAX_HEIGHT = 320;
const MAX_WIDTH = 480;

/** Fixed position under `anchor`, or above it when the space below is short. */
export function placeUnder(anchor: HTMLElement, align: "start" | "end" = "start"): CSSProperties {
  const r = anchor.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - GAP * 2;
  const above = r.top - GAP * 2;
  const up = below < 200 && above > below;
  const room = Math.min(MAX_HEIGHT, up ? above : below);
  const side =
    align === "end"
      ? { right: window.innerWidth - r.right, maxWidth: Math.min(MAX_WIDTH, r.right - GAP * 2) }
      : // Never past the window's right edge; long labels truncate instead.
        { left: r.left, maxWidth: Math.min(MAX_WIDTH, window.innerWidth - r.left - GAP * 2) };
  return {
    position: "fixed",
    minWidth: r.width,
    maxHeight: Math.max(room, 120),
    ...side,
    ...(up ? { bottom: window.innerHeight - r.top + GAP } : { top: r.bottom + GAP }),
  };
}

/** `placeUnder(anchor)` while mounted, kept up to date on scroll and resize. */
function useAnchoredPlace(anchor: RefObject<HTMLElement | null>, align: "start" | "end"): CSSProperties {
  const [place, setPlace] = useState<CSSProperties>({});
  useLayoutEffect(() => {
    if (!anchor.current) return;
    const update = () => anchor.current && setPlace(placeUnder(anchor.current, align));
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor, align]);
  return place;
}

type FloatingProps = HTMLAttributes<HTMLDivElement> & {
  /** What it hangs from; clicks on it do not dismiss. */
  anchor: RefObject<HTMLElement | null>;
  /** A mousedown outside the anchor and the panel. */
  onDismiss?: (() => void) | undefined;
  /** Line up with the anchor's left edge (default) or its right edge. */
  align?: "start" | "end";
  /** At least this wide (px); never narrower than the anchor. */
  minWidth?: number;
};

/** Mount it only while open: `{open && <Floating anchor={…}>…</Floating>}`. */
export const Floating = forwardRef<HTMLDivElement, FloatingProps>(function Floating(
  { anchor, onDismiss, align = "start", minWidth = 0, style, className = "", children, ...rest },
  ref,
) {
  const panel = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => panel.current!);
  const place = useAnchoredPlace(anchor, align);

  useEffect(() => {
    if (!onDismiss) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!anchor.current?.contains(t) && !panel.current?.contains(t)) onDismiss();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [anchor, onDismiss]);

  return createPortal(
    <div
      ref={panel}
      style={{ ...place, minWidth: Math.max(Number(place.minWidth ?? 0), minWidth), ...style }}
      className={`z-[60] rounded-[9px] border border-line bg-surface shadow-[var(--dialog-shadow)] ${className}`}
      {...rest}
    >
      {children}
    </div>,
    document.body,
  );
});
